import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import type { Task } from 'ember-concurrency';
import { and, gte, not } from 'ember-truth-helpers';
import GlobalFlowIcon from 'garaje/components/global-flow-icon';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import type Flow from 'garaje/models/flow';
import uniq from 'lodash/uniq';

interface SecurityIdScanningVisitorTypesSignature {
  Args: {
    onUpdate: ({ hasChanges }: { hasChanges: boolean }) => void;
    visitorTypes: Flow[];
    loadVisitorTypes: Task<Flow[], []>;
    faceMatchRequired?: boolean;
  };
  Element: HTMLElement;
}

export default class SecurityIdScanningVisitorTypes extends Component<SecurityIdScanningVisitorTypesSignature> {
  get enabledValues(): boolean[] {
    const flows = this.args.visitorTypes && this.args.visitorTypes.length ? this.args.visitorTypes : [];
    // Only consider non-global flows — globals are read-only and shouldn't
    // affect whether Select all / Deselect all links appear, since those
    // links only act on non-global flows.
    return uniq(flows.filter((vt) => !vt.isGlobalChild).map((vt) => vt.idScanPage.content?.enabled ?? false));
  }

  get allSelected(): boolean {
    return this.enabledValues.length === 1 && this.enabledValues[0] === true;
  }

  get noneSelected(): boolean {
    return this.enabledValues.length === 1 && this.enabledValues[0] === false;
  }

  // Whether the user has any toggleable (non-global) visitor types. Drives
  // whether Select all / Deselect all links should be shown — when false,
  // both links would be no-ops, so they're hidden.
  get hasNonGlobalFlows(): boolean {
    const flows = this.args.visitorTypes && this.args.visitorTypes.length ? this.args.visitorTypes : [];
    return flows.some((vt) => !vt.isGlobalChild);
  }

  deselectAll = (): void => {
    this.args.visitorTypes.forEach((vt) => {
      // Global visitor types are managed at the global overview and are
      // read-only here, so skip them when bulk-toggling.
      if (vt.isGlobalChild) return;
      if (!vt.idScanPage.content) return;
      vt.idScanPage.content.enabled = false;
    });
    this.triggerUpdated();
  };

  selectAll = (): void => {
    this.args.visitorTypes.forEach((vt) => {
      // Global visitor types are managed at the global overview and are
      // read-only here, so skip them when bulk-toggling.
      if (vt.isGlobalChild) return;
      if (!vt.idScanPage.content) return;
      vt.idScanPage.content.enabled = true;
    });
    this.triggerUpdated();
  };

  toggleEnabled = (visitorType: Flow): void => {
    // Defense in depth: the checkbox is already disabled in the template for
    // globals, but guard the action too so programmatic or future callers
    // can't mutate a read-only flow.
    if (visitorType.isGlobalChild) return;
    if (!visitorType.idScanPage.content) return;
    visitorType.idScanPage.content.enabled = !visitorType.idScanPage.content.enabled;
    this.triggerUpdated();
  };

  triggerUpdated = (): void => {
    this.args.onUpdate({ hasChanges: true });
  };

  <template>
    {{#if @loadVisitorTypes.isRunning}}
      <div class="mx-auto p-16" style="min-height: 180px; position: relative;">
        <LoadingSpinnerPulse />
      </div>
    {{else}}
      <div class="pb-4 mt-6 mr-16" ...attributes>
        <h4>Manage by visitor type</h4>
        <p class="mb-4">
          Select which visitor types require ID scanning.
        </p>
        {{#if (gte @visitorTypes.length 6)}}
          {{#if this.hasNonGlobalFlows}}
            {{#if this.allSelected}}
              <a
                href="javascript:void(0);"
                class="text-gem-50 text-xs mb-2"
                {{on "click" this.deselectAll}}
                data-test-deselect-all-visitors
              >Deselect all</a>
            {{else if this.noneSelected}}
              <a
                href="javascript:void(0);"
                class="text-gem-50 text-xs mb-2"
                {{on "click" this.selectAll}}
                data-test-select-all-visitors
              >Select all</a>
            {{else}}
              <a
                href="javascript:void(0);"
                class="text-gem-50 text-xs mb-2"
                {{on "click" this.selectAll}}
                data-test-select-all-visitors
              >Select all</a>
              <span class="text-carbon-50 text-xs mb-2">&ndash;</span>
              <a
                href="javascript:void(0);"
                class="text-gem-50 text-xs mb-2"
                {{on "click" this.deselectAll}}
                data-test-deselect-all-visitors
              >Deselect all</a>
            {{/if}}
          {{/if}}
        {{/if}}
        <ul class="list-none p-0 m-0 leading-normal">
          {{#each @visitorTypes as |visitorType|}}
            <li>
              <GlobalFlowIcon
                @show={{visitorType.isGlobalChild}}
                @tooltipText="Manage Visual Compliance for this global flow from the global overview."
              >
                <label class="-ml-1">
                  <input
                    data-test-idscan-vt={{visitorType.id}}
                    type="checkbox"
                    checked={{visitorType.idScanPage.content.enabled}}
                    {{on "click" (fn this.toggleEnabled visitorType)}}
                    disabled={{visitorType.isGlobalChild}}
                  />
                  {{visitorType.name}}
                </label>
              </GlobalFlowIcon>
              {{#if
                (and
                  @faceMatchRequired visitorType.idScanPage.content.enabled (not visitorType.photoPage.content.enabled)
                )
              }}
                <span data-test-idscan-vt-no-photo={{visitorType.id}} class="ml-2 text-papaya-60 text-xs">No visitor
                  photo capture enabled</span>
              {{/if}}
            </li>
          {{/each}}
        </ul>
      </div>
    {{/if}}
  </template>
}
